/**
 * The blackjack room's words that are not on its felt: the hand-end verdict, the
 * Train cards, and the one line the seat card carries.
 *
 * The same rules as `copy.ts` for poker (Phase 5): one line, about what just happened to
 * THIS player, one tap into the drill that sharpens it, and silence far more often than
 * not. The difference is the judge. Poker's verdict is a Monte-Carlo heuristic and says
 * "our model"; this room's is exact enumeration over the live shoe (`decide()`), so it
 * says "the exact play" — which is true, and a stronger claim than "the chart".
 */
import type { HeroMove } from "../table/blackjack-view";
import { INDIFFERENCE, type Decision } from "@engine/blackjack/decide";

/* ================================================================ the verdict === */

/**
 * A decision seen from the moves the table actually offered.
 *
 * `decide()` ranks everything the RULES allow. The room offers less when the stack is
 * short — a double or a split the player cannot match is not on the bar — and grading
 * against a move that was never a choice charged a short stack for being short: "the
 * exact play doubles there" under a greyed-out Double. So the ranking is cut to the
 * offered moves, the best of those is the best, and `costOf` reads it unchanged.
 */
export function restrictTo(d: Decision, offered: readonly HeroMove[]): Decision {
  const actions = d.actions.filter((a) => offered.includes(a.action));
  if (!actions.length || actions.length === d.actions.length) return d;
  const gap = actions.length > 1 ? actions[0]!.ev - actions[1]!.ev : Infinity;
  return { actions, best: actions[0]!.action, indifferent: gap < INDIFFERENCE };
}

/**
 * The floor under which the verdict says nothing: two per cent of the hand's bet.
 *
 * The analysis is exact, so there is no noise to hide — but a composition-dependent
 * nudge worth a tenth of a per cent is a fact about this shoe, not a habit to fix, and a
 * line that fired on it would teach the player to stop reading the line. Two per cent is
 * below every textbook mistake (standing on 16 against a 10 is about 0.03 of a bet, and
 * taking insurance about 0.04–0.08) and above almost every deviation.
 */
export const BJ_VERDICT_FLOOR = 0.02;

export interface BjGraded {
  /** What the exact analysis ranked first, and what the player did. */
  best: HeroMove;
  took: HeroMove;
  /** Phase 9: what basic strategy says for the same position (`basicAction`)… */
  chart?: HeroMove;
  /** …and what the chart's own move gives up against the exact best, in the same units. */
  chartCost?: number;
  /** In bet units per unit bet (what `costOf` returns). */
  cost: number;
  /** The hand's opening bet, in units — what the cost is paid on. */
  bet: number;
}

export interface BjVerdict {
  /** The whole line, minus the drill's name, which the component links. */
  text: string;
  /** In Bucks, for the report and the tests. */
  costBucks: number;
  drill: string;
  href: string;
}

const VERB: Record<HeroMove, string> = {
  hit: "hits", stand: "stands", double: "doubles", split: "splits",
  insurance: "takes insurance", decline: "declines insurance",
};

/**
 * One line at hand end, or nothing. Names the ONE worst decision of the hand, and only
 * when the exact play was something else and it cost at least `BJ_VERDICT_FLOOR`.
 *
 * PHASE 9 (master plan §9): the exact play and its cost are Blackjack Pro. Without it
 * (`exact: false`) the line is basic strategy's — "Basic strategy stands there." — for
 * the costliest decision of the hand that left the chart, with no number in it. It fires
 * only when leaving the chart cost at least the floor MORE than following it would have
 * (`cost - chartCost`): a departure the live deck made right, or near enough, is not
 * called a mistake.
 */
export function bjVerdictLine(
  graded: readonly BjGraded[], fmt: (bucks: number) => string, exact = true,
): BjVerdict | null {
  const card = BJ_TRAIN_CARDS.find((c) => c.id === "basic")!;
  if (!exact) {
    let off: BjGraded | null = null;
    const worse = (g: BjGraded) => g.cost - (g.chartCost ?? 0);
    for (const g of graded) {
      if (!g.chart || g.chart === g.took || !(worse(g) >= BJ_VERDICT_FLOOR)) continue;
      if (!off || worse(g) * g.bet > worse(off) * off.bet) off = g;
    }
    if (!off) return null;
    return { text: `Basic strategy ${VERB[off.chart!]} there.`, costBucks: worse(off) * off.bet * 25, drill: card.id, href: card.href };
  }
  let worst: BjGraded | null = null;
  for (const g of graded) if (!worst || g.cost * g.bet > worst.cost * worst.bet) worst = g;
  if (!worst || worst.best === worst.took) return null;
  if (!(worst.cost >= BJ_VERDICT_FLOOR)) return null;
  const bucks = worst.cost * worst.bet * 25;
  return {
    text: `The exact play ${VERB[worst.best]} there — cost ${fmt(bucks)}.`,
    costBucks: bucks, drill: card.id, href: card.href,
  };
}

/* ============================================================ the Train cards === */

export interface BjTrainCard {
  id: "basic" | "count" | "spots";
  title: string;
  line: string;
  href: string;
  /** Phase 9 (master plan §9): part of Blackjack Pro. */
  pro: boolean;
}

/**
 * The room's three drills (master plan §4). Each is small, and each says what it is.
 * The Count Drill in particular says the thing the room says about itself: two decks,
 * reshuffled before every hand, so a count is an exercise here and never an edge.
 */
export const BJ_TRAIN_CARDS: readonly BjTrainCard[] = [
  {
    id: "basic", title: "Basic Strategy Drill",
    line: "A hand and an up-card, dealt from a fresh shoe. Say what the chart says, and see whether you matched it.",
    href: "/blackjack/train/run?card=basic", pro: false,
  },
  {
    id: "count", title: "Count Drill",
    line: "Keep a running count while a table's cards come out. An exercise only: this room reshuffles before every hand.",
    href: "/blackjack/train/run?card=count", pro: true,
  },
  {
    id: "spots", title: "Decision Spots",
    line: "Hands where the deck on the table changes the answer. Pick the play; the exact numbers show why.",
    href: "/blackjack/train/run?card=spots", pro: true,
  },
];

/** The Ledger's one drill for a blackjack sitting: the chart, because that is where it went. */
export const BJ_NEXT_DRILL = BJ_TRAIN_CARDS[0]!;

/** The seat card's line about the house. */
export const bjDealerLine = (dealer: string): string =>
  `${dealer} deals. Two decks, a new shuffle before every hand, and a verdict on every decision.`;
